import * as eaiws from '@easterngraphics/wcf/modules/eaiws';
import * as core from '@easterngraphics/wcf/modules/core';
import * as cf from '@easterngraphics/wcf/modules/cf';
import * as catalog from '@easterngraphics/wcf/modules/eaiws/catalog';
import { InsertElements } from '@easterngraphics/wcf/modules/core/cmd';
import { importPec, importPecFromCatalog } from '@easterngraphics/wcf/modules/cf/io';
import { Log, wcfConfig } from '@easterngraphics/wcf/modules/utils';
import { getUrlParamValue, isNotNullOrEmpty, isNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';
import { ajax } from '@easterngraphics/wcf/modules/utils/async';
import '@easterngraphics/wcf/modules/polyfill/core-js';

import { Engine } from '@babylonjs/core';

import { CatalogUI } from './components/catalog';
import { BasketUI } from './components/basket';
import { PropertyEditorUI } from './components/property-editor';
import { ViewerUI } from './components/viewer';
import { OapUI } from './components/oap';
import { ExportUI } from './components/export';
import { PersistenceUI } from './components/persistence';
import { ProgressUI } from './components/progress';
import { ControlUI } from './components/controls';
import './index.css';
import { HtmlUtils, isIOSBrowser } from './utils';
import { SceneElement } from '@easterngraphics/wcf/modules/core/mdl';
import { getCurrentLanguage } from './helpers/i18n';
import { buildAssetUrl, buildShareGetUrl, runtimeConfig } from './helpers/runtime-config';
import { reportBootStage, reportBooted, reportViewerFailure, reportViewerReady } from './helpers/diagnostics';

export class App {
    private readonly DEFAULT_GATEKEEPER_ID: string = '5fc7a7fcd1644';
    private readonly CURRENT_VARIANT_CODE_STORAGE_KEY: string = 'currentVariantCode';
    private gatekeeperId: string | undefined;
    private session: eaiws.EaiwsSession;
    private basketUI: BasketUI;
    private viewerUI: ViewerUI;
    private coreApp: core.Application;
    private articleManager: cf.ArticleManager;
    private catalogUI: CatalogUI;
    private propertyEditorUI: PropertyEditorUI;
    private oapUI: OapUI;
    private exportUI: ExportUI;
    private persistenceUI: PersistenceUI;
    private controlUI: ControlUI;

    constructor() {
        void this.init();
    }

    public async init(): Promise<void> {
        reportBootStage('boot-start', { readyState: document.readyState, assetBaseUrl: runtimeConfig.assetBaseUrl });
        if (!Engine.isSupported()) {
            reportViewerFailure({ reason: 'webgl-unsupported' });
            alert('WebGL required!');
            return;
        }
        if (!isIOSBrowser() && document.documentElement != null) {
            document.documentElement.classList.add('wcf-hover-enabled');
        }

        this.syncBootstrapToLocalStorage();
        const viewerContainer = await this.waitForViewerContainer();
        ProgressUI.beginLoading();
        reportBootStage('session-start');
        this.session = await this.createSession();
        await this.applyLanguagePreferences();
        reportBootStage('session-ready', { gatekeeperId: this.gatekeeperId ?? null });

        this.coreApp = new core.Application();
        this.coreApp.applicationName = 'WCF-DEV-EXAMPLE';
        this.coreApp.applicationVersion = '1.0.0';
        wcfConfig.dataPath = buildAssetUrl('w-cf/data/');
        const appOptions: Partial<core.AppInitOptions> = {
            disableWebGL2Support: true,
        };
        this.coreApp.initialize(viewerContainer, appOptions);
        Log.info('W-CF ' + this.coreApp.version + ' build: ' + this.coreApp.buildInfo);
        reportBootStage('viewer-initialized', { version: this.coreApp.version, buildInfo: this.coreApp.buildInfo });

        this.articleManager = new cf.ArticleManager(this.coreApp, this.session, { oapPlanningMode: false });
        this.articleManager.setGfjBasketIdsEnabled(true);

        this.viewerUI = new ViewerUI(
            this.coreApp,
            {
                disableCameraPanning: false,
                limitCameraDistanceByElementRadius: false
            },
            this.session,
            this.articleManager
        );
        this.catalogUI = new CatalogUI(
            HtmlUtils.getNotNullElementById('catalog'),
            this.session.catalog,
            this.onInsertCatalogArticle.bind(this),
            this.onInsertCatalogContainer.bind(this)
        );
        this.propertyEditorUI = new PropertyEditorUI(
            HtmlUtils.getNotNullElementById('property-editor'),
            this.coreApp.model.selectionProperties
        );
        this.oapUI = new OapUI(
            HtmlUtils.getNotNullElementById('oap'),
            this.coreApp.model.selectionProperties,
            this.coreApp.appCallbacks
        );
        this.basketUI = new BasketUI(
            HtmlUtils.getNotNullElementById('basket'),
            this.articleManager,
            this.onBasketItemClicked.bind(this)
        );
        this.exportUI = new ExportUI(
            HtmlUtils.getNotNullElementById('export'),
            this.articleManager,
            this.coreApp.viewer
        );
        this.persistenceUI = new PersistenceUI(
            HtmlUtils.getNotNullElementById('persistence'),
            this.articleManager,
            this.onLoadFile.bind(this)
        );
        this.controlUI = new ControlUI(this.coreApp, this.articleManager);
        reportBooted({
            gatekeeperId: this.gatekeeperId ?? null,
            bootstrapBaseArticleNumber: runtimeConfig.bootstrapBaseArticleNumber ?? null,
        });

        const startupVariantCode = this.consumeStartupVariantCode();
        const importedSelection = await this.parseParameter();
        if (!importedSelection && this.gatekeeperId === this.DEFAULT_GATEKEEPER_ID) {
            await this.insertInitialArticle(startupVariantCode);
        }
        ProgressUI.endLoading();
    }

    /**
     * Creates a session using gatekeeper id or a direct connection to eaiws via baseurl and startup.
     * To use a direct connection to eaiws, just set the search parameters baseurl and startup like:
     * http://localhost:3000/?baseurl=https://my-eaiws-server/&startup=my_startup
     */
    private async createSession(): Promise<eaiws.EaiwsSession> {
        const baseUrl = getUrlParamValue(window.location.href, 'baseurl');
        const startup = getUrlParamValue(window.location.href, 'startup');
        const useStartup = isNotNullOrEmpty(baseUrl) && isNotNullOrEmpty(startup);
        if (useStartup) {
            return this.createSessionWithStartup(baseUrl, startup);
        }
        this.gatekeeperId = this.getFromUrlOrDefault('gatekeeper_id', this.DEFAULT_GATEKEEPER_ID);
        return this.createSessionWithGatekeeperId(this.gatekeeperId);
    }

    /**
     * Creates a direct connection to eaiws with a baseurl and startup.
     */
    private async createSessionWithStartup(baseUrl: string, startup: string): Promise<eaiws.EaiwsSession> {
        if (isNullOrEmpty(baseUrl)) {
            throw new Error('baseurl missing');
        }
        if (isNullOrEmpty(startup)) {
            throw new Error('startup missing');
        }
        const session = new eaiws.EaiwsSession();
        await session.open(baseUrl, { startup: startup });
        return session;
    }

    /**
     * Create session with gatekeeper, details here: https://eaiws-server.pcon-solutions.com/doc/v3/
     */
    private async createSessionWithGatekeeperId(gatekeeperId: string): Promise<eaiws.EaiwsSession> {
        let gatekeeperResponse: { keepAliveInterval: number; server: string; sessionId: string };
        const locale = 'de_DE';
        try {
            gatekeeperResponse =
                await ajax(
                    'POST',
                    'https://eaiws-server.pcon-solutions.com/v3/session/' + gatekeeperId,
                    {
                        locale,
                    },
                    {
                        dataType: 'json',
                        retryAttempts: 1,
                        ignoreGlobalErrorHandler: true,
                        timeout: 10000,
                    }
                );
        } catch (error) {
            Log.info('Failed to start gatekeeper session, using fallback server: ' + (error as string));
            gatekeeperResponse = await ajax(
                'POST',
                'https://eaiws-server-fallback.pcon-solutions.com/v3/session/' + gatekeeperId,
                {
                    locale,
                },
                {
                    dataType: 'json',
                }
            );
        }
        const session = new eaiws.EaiwsSession();
        session.connect(
            gatekeeperResponse.server,
            gatekeeperResponse.sessionId,
            gatekeeperResponse.keepAliveInterval * 1000
        );
        return session;
    }

    private getFromUrlOrDefault(urlParam: string): string | undefined;
    private getFromUrlOrDefault(urlParam: string, defaultValue: string): string;
    private getFromUrlOrDefault(urlParam: string, defaultValue?: string): string | undefined {
        const fromUrl: string | null = getUrlParamValue(window.location.href, urlParam);
        if (isNotNullOrEmpty(fromUrl)) {
            return fromUrl;
        }

        return defaultValue;
    }

    private async onInsertCatalogArticle(article: catalog.ArticleCatalogItem): Promise<void> {
        console.log('App::onInsertCatalogArticle', article);
        ProgressUI.beginLoading();
        await this.removeAllElements();

        const element: cf.MainArticleElement = await this.articleManager.insertArticle(article);
        this.coreApp.model.addElement(element);
        this.coreApp.model.setSelection([element]);
        this.persistCatalogArticleMetadata(article);
        this.viewerUI.resetCamera();
        this.viewerUI.allowMainArticleSelection(this.coreApp.model.elements.length > 1);
        await this.basketUI.updateBasket();
        await this.finalizeViewerBootstrap('catalog-article');
        ProgressUI.endLoading();
    }

    private async onInsertCatalogContainer(container: catalog.CatalogItem): Promise<void> {
        console.log('App::onInsertCatalogContainer', container);
        ProgressUI.beginLoading();
        await this.removeAllElements();

        const elements: SceneElement[] = await importPecFromCatalog(this.articleManager, container);
        elements.forEach((element) => this.coreApp.model.addElement(element));

        this.viewerUI.resetCamera();
        this.viewerUI.allowMainArticleSelection(this.coreApp.model.elements.length > 1);
        await this.basketUI.updateBasket();
        await this.persistMainArticleMetadata(this.articleManager.getAllMainArticles()[0] ?? null);
        await this.finalizeViewerBootstrap('catalog-container');
        ProgressUI.endLoading();
    }

    private async removeAllElements(): Promise<void> {
        while (this.coreApp.model.elements.length > 0) {
            this.coreApp.model.removeElement(this.coreApp.model.elements[0]);
        }
        await this.articleManager.synchronizeSession(false);
    }

    /**
     * Select article item if it was clicked in the basket.
     */
    private onBasketItemClicked(item: cf.ArticleElement, event: MouseEvent): void {
        console.log('App::onBasketItemClicked', item);
        event.stopImmediatePropagation();
        if (item instanceof cf.MainArticleElement) {
            this.coreApp.model.setSelection([item]);
        } else if (item instanceof cf.SubArticleElement) {
            this.coreApp.model.setSubElementSelection(item.getMainArticle(), item);
        }
    }

    /**
     * Loads an old planning.
     * @param file Loaded .obk/.pec file as binary
     */
    private async onLoadFile(file: Blob, fileType: 'obk' | 'pec'): Promise<void> {
        ProgressUI.beginLoading();
        this.coreApp.document.clear();
        if (fileType === 'obk') {
            const url: string | null = await this.articleManager.session.uploadFile('Project', file);
            if (isNullOrEmpty(url)) {
                alert('failed to upload file');
                return;
            }
            await this.session.session.loadSession(url);
            await this.session.deleteFile(url);
            await this.articleManager.importFromSession({ importArticlesWithoutPosition: true });
        } else if (fileType === 'pec') {
            try {
                const elements: SceneElement[] = await importPec(this.articleManager, file);
                const insertCommand: InsertElements = new InsertElements(this.coreApp, elements);
                await this.coreApp.commands.executeCommand(insertCommand);
            } catch (e) {
                console.error('failed to load pec', e);
            }
        }
        this.viewerUI.resetCamera();
        await this.basketUI.updateBasket();
        if (this.coreApp.model.elements.length > 0) {
            this.coreApp.model.setSelection([this.coreApp.model.elements[0]]);
            await this.persistMainArticleMetadata(this.articleManager.getAllMainArticles()[0] ?? null);
        }
        this.viewerUI.allowMainArticleSelection(this.coreApp.model.elements.length > 1);
        await this.finalizeViewerBootstrap(`load-file:${fileType}`);
        ProgressUI.endLoading();
    }

    private async insertInitialArticle(startupVariantCode?: string): Promise<void> {
        const lookupOptions: catalog.LookupOptions = new catalog.LookupOptions();
        lookupOptions.displayMode = 'All';
        const candidateBaseArticleNumbers = this.getConfiguredBaseArticleNumbers();

        for (const baseArticleNumber of candidateBaseArticleNumbers) {
            const searchParameterSet: catalog.SearchArticleParameterSet = new catalog.SearchArticleParameterSet();
            searchParameterSet.catalogIds = [runtimeConfig.defaultCatalogId];
            searchParameterSet.baseArticleNumber = baseArticleNumber;
            searchParameterSet.numberOfHits = 1;

            const foundItems: catalog.TopCatalogItems | undefined = await this.session.catalog.searchArticle(searchParameterSet, lookupOptions);
            const item: catalog.CatalogItem | undefined = foundItems?.scoredItems[0]?.item;
            if (!(item instanceof catalog.ArticleCatalogItem)) {
                continue;
            }

            item.varCodeType = 'OFML';
            item.variantCode = startupVariantCode ?? '';
            await this.onInsertCatalogArticle(item);
            return;
        }

        localStorage.removeItem('currentBaseArticleNumber');
        alert('Article could not be found.');
    }

    private consumeStartupVariantCode(): string | undefined {
        const variantCode = runtimeConfig.startupVariantCode ?? localStorage.getItem(this.CURRENT_VARIANT_CODE_STORAGE_KEY);
        localStorage.removeItem(this.CURRENT_VARIANT_CODE_STORAGE_KEY);
        return variantCode === null || variantCode === '' ? undefined : variantCode;
    }

    private async applyLanguagePreferences(): Promise<void> {
        const currentLanguage = getCurrentLanguage();
        const languages = [currentLanguage, 'de', 'en', 'fr'].filter((value, index, allValues) => allValues.indexOf(value) === index);
        await this.session.catalog.setLanguages(languages);
        await this.session.basket.setLanguages(languages);
    }

    private async parseParameter(): Promise<boolean> {
        const selection = getUrlParamValue(window.location.href, 'selection');
        if (isNullOrEmpty(selection)) {
            return false;
        }

        const shareGetUrl = buildShareGetUrl(selection);
        if (shareGetUrl == null) {
            console.warn('selection parameter found but no shareGetUrlBase is configured');
            return false;
        }

        try {
            const response = await fetch(shareGetUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch shared configuration: ${response.status}`);
            }
            const payload = await response.text();
            await this.insertObx(new Blob([payload], { type: 'application/xml' }));
            return true;
        } catch (error) {
            console.error('Failed to import shared configuration', error);
            alert('Failed to import shared configuration.');
            return false;
        }
    }

    private async insertObx(obx: Blob): Promise<void> {
        ProgressUI.beginLoading();
        try {
            await this.removeAllElements();
            const mainArticles: Array<cf.MainArticleElement> = await this.articleManager.importObx(
                obx,
                { importArticlesWithoutPosition: true }
            );
            mainArticles.forEach((mainArticle) => {
                this.coreApp.model.addElement(mainArticle);
            });

            if (mainArticles.length > 0) {
                this.coreApp.model.setSelection([mainArticles[0]]);
                await this.persistMainArticleMetadata(mainArticles[0]);
            }

            this.viewerUI.resetCamera();
            this.viewerUI.allowMainArticleSelection(this.coreApp.model.elements.length > 1);
            await this.basketUI.updateBasketByMainArticle(mainArticles);
            await this.finalizeViewerBootstrap('obx-import');
        } finally {
            ProgressUI.endLoading();
        }
    }

    private persistCatalogArticleMetadata(article: catalog.ArticleCatalogItem): void {
        localStorage.setItem('currentBaseArticleNumber', article.baseArticleNumber);
        if (isNotNullOrEmpty(article.label)) {
            localStorage.setItem('currentBaseArticleName', article.label);
        }
        this.viewerUI.updateTitle();
    }

    private async persistMainArticleMetadata(article: cf.MainArticleElement | null): Promise<void> {
        if (article == null) {
            this.viewerUI.updateTitle();
            return;
        }

        const itemProperties = await article.getItemProperties();
        if (isNotNullOrEmpty(itemProperties.article?.baseArticleNumber)) {
            localStorage.setItem('currentBaseArticleNumber', itemProperties.article.baseArticleNumber);
        }

        const articleName = itemProperties.article?.shortText ?? itemProperties.article?.longText ?? '';
        if (isNotNullOrEmpty(articleName)) {
            localStorage.setItem('currentBaseArticleName', articleName);
        }

        this.viewerUI.updateTitle();
    }

    private syncBootstrapToLocalStorage(): void {
        if (isNotNullOrEmpty(runtimeConfig.bootstrapBaseArticleNumber)) {
            localStorage.setItem('currentBaseArticleNumber', runtimeConfig.bootstrapBaseArticleNumber);
        }

        if (isNotNullOrEmpty(runtimeConfig.bootstrapBaseArticleName)) {
            localStorage.setItem('currentBaseArticleName', runtimeConfig.bootstrapBaseArticleName);
        }

        if (isNotNullOrEmpty(runtimeConfig.bootstrapLanguage)) {
            localStorage.setItem('currentLanguage', runtimeConfig.bootstrapLanguage);
        }
    }

    private async waitForViewerContainer(): Promise<HTMLDivElement> {
        const viewerContainer = HtmlUtils.getNotNullElementById('pcon-viewer') as HTMLDivElement;
        const size = await HtmlUtils.waitForElementSize(viewerContainer, 80, 240, 5000);
        reportBootStage('viewer-container-ready', {
            width: size?.width ?? 0,
            height: size?.height ?? 0,
        });
        return viewerContainer;
    }

    private getConfiguredBaseArticleNumbers(): string[] {
        return [
            runtimeConfig.bootstrapBaseArticleNumber,
            localStorage.getItem('currentBaseArticleNumber'),
            runtimeConfig.defaultBaseArticleNumber
        ].filter((value, index, allValues): value is string => value != null && value !== '' && allValues.indexOf(value) === index);
    }

    private async finalizeViewerBootstrap(context: string): Promise<void> {
        const viewerReady = await this.viewerUI.ensureReady(context);
        if (viewerReady) {
            reportViewerReady({
                context,
                elements: this.coreApp.model.elements.length,
            });
            return;
        }

        reportViewerFailure({
            context,
            elements: this.coreApp.model.elements.length,
        });
    }
}

function bootApp(): void {
    window.app = new App();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootApp, { once: true });
} else {
    bootApp();
}
